import {
  isFulfilled,
  type Middleware,
  type ThunkDispatch,
  type UnknownAction,
} from '@reduxjs/toolkit'
import type { studioApi } from '@/features/ai/studioApi'
import type { blocksApi } from '@/features/editor/blocksApi'
import type { coursesApi } from '@/features/editor/coursesApi'
import type { lessonsApi } from '@/features/editor/lessonsApi'
import type { videosApi } from '@/features/editor/videosApi'
import { draftSourceSettled, setDraftSourceSettling } from '@/features/editor/editorSlice'
import { refetchCourseModeration } from './lib/refetchCourseModeration'
import { probeOutcomeOf, type ProbeOutcome } from './lib/draftSourceSettling'
import { createSettlingWatchdog, isSourceBehind, type WatchState } from './lib/settlingWatchdog'

// Имена проверяются компилятором против настоящих эндпоинтов: переименованная ручка
// ломает сборку, а не тихо выпадает из зонда (и черновик перестаёт замечаться).
type ContentWriteEndpoint =
  | keyof typeof coursesApi.endpoints
  | keyof typeof lessonsApi.endpoints
  | keyof typeof blocksApi.endpoints
  | keyof typeof videosApi.endpoints
  | keyof typeof studioApi.endpoints

/**
 * Мутации, которые пишут КОНТЕНТ курса (резолвер бэка создаёт по ним черновик).
 * Сквозные поля (`updateCourse` с ценой) тоже попадают сюда: лишний `GET` безвреден,
 * а угадывать по телу `PUT`, контентное ли поле, — вторая копия
 * `NON_CONTENT_COURSE_FIELDS` бэка.
 */
export const CONTENT_WRITE_ENDPOINTS: ReadonlySet<string> = new Set<ContentWriteEndpoint>([
  'updateCourse',
  'changeSourceLang',
  'declareTranslation',
  'deleteTranslation',
  'setTranslationPublished',
  'createLesson',
  'updateLesson',
  'deleteLesson',
  'reorderLessons',
  'createBlock',
  'updateBlock',
  'deleteBlock',
  'reorderBlocks',
  'updateBlocksLook',
  'deleteVideo',
  'startCourseGeneration',
  'startBlockEdit',
  'startImageGeneration',
  'startTranslation',
  'resumeJob',
])

type ProbeState = WatchState & {
  editor: { draftProbeCourseId: number | null; draftSourceSettling: number | null }
}

type ProbeDispatch = ThunkDispatch<ProbeState, unknown, UnknownAction>

/** Перечитать `/moderation`; упавший запрос повторяется ОДИН раз. */
const probeOnce = async (
  dispatch: ProbeDispatch,
  getState: () => ProbeState,
  courseId: number,
): Promise<ProbeOutcome> => {
  const first = probeOutcomeOf(await refetchCourseModeration(dispatch, getState, courseId))
  if (first !== 'failed') return first
  return probeOutcomeOf(await refetchCourseModeration(dispatch, getState, courseId))
}

interface MutationMeta {
  arg?: { type?: string; endpointName?: string }
}

const isContentWrite = (meta: MutationMeta): boolean =>
  meta.arg?.type === 'mutation' && CONTENT_WRITE_ENDPOINTS.has(meta.arg.endpointName ?? '')

/**
 * Зонд первого черновика: пока живой курс редактора без черновика
 * (`editor.draftProbeCourseId`), каждая успешная пишущая мутация перечитывает
 * `/moderation`. Появившийся `draftId` ловит `ModerationPoller` и переводит чтение
 * редактора на черновик.
 */
export const draftProbeMiddleware: Middleware<object, ProbeState, ProbeDispatch> = ({
  dispatch,
  getState,
}) => {
  // Решает только ПОСЛЕДНИЙ зонд: ответ раннего (ушёл до второй записи) не вправе снять
  // признак, пока поздний ещё летит.
  let generation = 0
  // Курс, чей последний зонд ещё летит: пока он не ответил, признак не снимается никем —
  // переезд на живой мог закончиться, а запись уже завела на бэке новый черновик.
  let holding: number | null = null
  // Сторож — на ЛЮБУЮ установку признака (зонд, поллер): переезд мог не случиться вовсе.
  // Его срабатывание держание не перебивает: лок не бывает вечным.
  const watch = createSettlingWatchdog(dispatch, () => {
    holding = null
  })
  const isHeld = (action: unknown): boolean =>
    draftSourceSettled.match(action) &&
    action.payload === holding &&
    getState().editor.draftProbeCourseId === holding
  return (next) => (action) => {
    // Снятие при летящем зонде ГЛОТАЕТСЯ, а не перебивается повторной установкой: иначе
    // `waitDraftSourceSettled` (коммит загрузки) успел бы увидеть мгновенный `null`.
    // Размонтирование поллера сначала снимает зонд — его снятие проходит.
    if (isHeld(action)) return action
    const result = next(action)
    watch(action, getState())
    if (!isFulfilled(action) || !isContentWrite(action.meta as MutationMeta)) return result
    const courseId = getState().editor.draftProbeCourseId
    if (courseId === null) return result
    // Признак встаёт СИНХРОННО, до перечитывания: первая запись живого курса уже завела
    // черновик, и пакетная запись, собранная из живой версии, затёрла бы его.
    const mine = ++generation
    holding = courseId
    dispatch(setDraftSourceSettling(courseId))
    // Летящий запрос (ушёл до записи) помощник дожидается и повторяет. Второй подписки
    // ради этого не заводить: запрос `/moderation` ведёт один `ModerationPoller`.
    // Ответ: нет черновика или две ошибки подряд — снять (черновик позже поймает опрос
    // поллера); черновик, а редактор ещё не на нём — признак остаётся до переезда
    // (`useReadSourceRemap`); черновик, и редактор уже читает его (переезд закончился,
    // пока признак держали) — снять, иначе он стоял бы до сторожа.
    void probeOnce(dispatch, getState, courseId).then((outcome) => {
      if (mine !== generation) return
      holding = null
      if (outcome !== 'draft' || !isSourceBehind(getState(), courseId))
        dispatch(draftSourceSettled(courseId))
    })
    return result
  }
}
